import { reportingIdentifier } from 'src/queries/reporting/reporting.identifier'
import { parseReportingSnapshot } from 'src/queries/reporting/reporting.validation'
import type {
  DraftDisplayProjection,
  DraftSubmissionProjection,
  ReportingRunProjection,
} from 'src/queries/reportingAgent/reportingAgent.models'
import { REPORTING_AGENT_VERSION } from 'src/queries/reportingAgent/reportingAgent.models'

import {
  eventsUrl,
  exact,
  isoDate,
  nullableIdentifier,
  object,
  string,
  version,
} from './primitives'

export const parseDraftSubmissionProjection = (
  value: unknown,
): DraftSubmissionProjection => {
  const source = object(value, 'draft submission')
  exact(
    source,
    [
      'version',
      'conversation_id',
      'message_id',
      'run_id',
      'status',
      'events_url',
    ],
    'draft submission',
  )
  version(source.version, 'draft submission')
  if (source.status !== 'queued' && source.status !== 'running')
    throw new Error('draft submission.status is invalid')
  const status = source.status
  return {
    version: REPORTING_AGENT_VERSION,
    conversation_id: reportingIdentifier(
      source.conversation_id,
      'draft submission.conversation_id',
    ),
    message_id: reportingIdentifier(
      source.message_id,
      'draft submission.message_id',
    ),
    run_id: reportingIdentifier(source.run_id, 'draft submission.run_id'),
    status: status as 'queued' | 'running',
    events_url: eventsUrl(source.events_url, 'draft submission.events_url'),
  }
}

export const parseMessageSubmissionProjection = (value: unknown) => {
  const source = object(value, 'message submission')
  exact(
    source,
    ['version', 'message_id', 'run_id', 'status', 'events_url'],
    'message submission',
  )
  version(source.version, 'message submission')
  if (source.status !== 'queued' && source.status !== 'running')
    throw new Error('message submission.status is invalid')
  const status = source.status
  return {
    version: REPORTING_AGENT_VERSION,
    message_id: reportingIdentifier(
      source.message_id,
      'message submission.message_id',
    ),
    run_id: reportingIdentifier(source.run_id, 'message submission.run_id'),
    status: status as 'queued' | 'running',
    events_url: eventsUrl(source.events_url, 'message submission.events_url'),
  }
}

export const parseRunProjection = (value: unknown): ReportingRunProjection => {
  const source = object(value, 'run')
  exact(
    source,
    [
      'version',
      'id',
      'conversation_id',
      'trigger_message_id',
      'status',
      'artifact_id',
      'created_at',
      'updated_at',
    ],
    'run',
  )
  version(source.version, 'run')
  const statuses = ['queued', 'running', 'completed', 'failed', 'cancelled']
  if (typeof source.status !== 'string' || !statuses.includes(source.status))
    throw new Error('run.status is invalid')
  return {
    version: REPORTING_AGENT_VERSION,
    id: reportingIdentifier(source.id, 'run.id'),
    conversation_id: reportingIdentifier(
      source.conversation_id,
      'run.conversation_id',
    ),
    trigger_message_id: reportingIdentifier(
      source.trigger_message_id,
      'run.trigger_message_id',
    ),
    status: source.status as ReportingRunProjection['status'],
    artifact_id: nullableIdentifier(source.artifact_id, 'run.artifact_id'),
    created_at: isoDate(source.created_at, 'run.created_at'),
    updated_at: isoDate(source.updated_at, 'run.updated_at'),
  }
}

export const parseDraftDisplayProjection = (
  value: unknown,
): DraftDisplayProjection => {
  const source = object(value, 'draft display')
  exact(
    source,
    [
      'version',
      'artifact_id',
      'live_report_candidate_id',
      'conversation_id',
      'title',
      'generated_at',
      'result_expires_at',
      'chart_spec',
      'dataset',
    ],
    'draft display',
  )
  version(source.version, 'draft display')
  const snapshot = parseReportingSnapshot(source.chart_spec, source.dataset)
  return {
    version: REPORTING_AGENT_VERSION,
    artifact_id: reportingIdentifier(
      source.artifact_id,
      'draft display.artifact_id',
    ),
    live_report_candidate_id: (() => {
      if (
        source.live_report_candidate_id === undefined ||
        source.live_report_candidate_id === null
      ) {
        return null
      }
      return reportingIdentifier(
        source.live_report_candidate_id,
        'draft display.live_report_candidate_id',
      )
    })(),
    conversation_id: reportingIdentifier(
      source.conversation_id,
      'draft display.conversation_id',
    ),
    title: (() => {
      const title = string(source.title, 'draft display.title')
      if (title.length < 1 || title.length > 256)
        throw new Error(
          'draft display.title must be between 1 and 256 characters',
        )
      return title
    })(),
    generated_at: isoDate(source.generated_at, 'draft display.generated_at'),
    result_expires_at:
      source.result_expires_at === null
        ? null
        : isoDate(source.result_expires_at, 'draft display.result_expires_at'),
    chart_spec: snapshot.chart_spec,
    dataset: snapshot.dataset,
  }
}
